<template>
  <Head>
    <Meta v-if="dev" name="robots" content="noindex" />
  </Head>

  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>

  <TransitionsFadeTransition>
    <PhotoModal
      v-if="mainStore.photoModal.isVisible"
      :photos="mainStore.photoModal.photos"
      :model-value="mainStore.photoModal.selectedPhotoId"
      @close="mainStore.hidePhotoModal()"
    />
  </TransitionsFadeTransition>

  <TransitionsFadeTransition>
    <TripPhotoModal
      v-if="mainStore.tripPhotoModal.isVisible && mainStore.tripPhotoModal.tripId"
      :trip-id="mainStore.tripPhotoModal.tripId"
      :selected-photo-id="mainStore.tripPhotoModal.selectedPhotoId"
      @close="mainStore.hideTripPhotoModal()"
    />
  </TransitionsFadeTransition>

  <HideShow v-model="showErrorDialog" :timeout="3000">
    <template #content>
      <div v-if="errorMessage" class="error">
        {{ t('error') }}
        :
        {{ t(errorMessage) }}
      </div>
    </template>
  </HideShow>

  <RelationshipFollowerIntroDialog
    v-if="showFollowerIntroDialog"
    @saved="showFollowerIntroDialog = false"
  />

  <RelationshipHomebasePromptDialog
    v-if="showHomebaseDialog"
    :initial-homebase="homebaseDialogInitialValue"
    @saved="showHomebaseDialog = false"
    @close="showHomebaseDialog = false"
  />

  <InteractionsLikeListModal
    v-if="
      mainStore.likeListModal.isVisible &&
      mainStore.likeListModal.objectId &&
      mainStore.likeListModal.objectType
    "
    :object-id="mainStore.likeListModal.objectId"
    :object-type="mainStore.likeListModal.objectType"
    :object-parent-id="mainStore.likeListModal.objectParentId"
    @close="mainStore.hideLikeListModal()"
  />

  <ReferralInviteFriendModal
    v-if="mainStore.inviteFriendModal.isVisible"
    @close="mainStore.hideInviteFriendModal()"
  />

  <Modal v-if="mainStore.alertModal.isVisible" force-answer @close="mainStore.dismissAlertModal()">
    <template #header>
      <span class="tw-font-medium">{{ mainStore.alertModal.title }}</span>
    </template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-6 tw-py-4">
        {{ mainStore.alertModal.message }}

        <InputAppButton
          class="tw-flex-grow tw-rounded-full tw-bg-riser-blue tw-px-4 tw-py-2 tw-text-center tw-text-white"
          @click="mainStore.dismissAlertModal()"
        >
          {{ t('Close') }}
        </InputAppButton>
      </div>
    </template>
  </Modal>

  <CookieControl
    v-if="cookieBannerReady && !isLoadedFromApp && !rewind"
    :locale="locale.substring(0, 2) ?? 'en'"
  />
</template>

<script setup>
import { retryRequest } from '~/data/api'
import { setErrorHandler } from '~/services/responseHandler'
import { countryOptions } from '~/data/countryOptions'

const authStore = useAuthStore()
const mainStore = useMainStore()
const garageStore = useGarageStore()

const { t, locale } = useI18n()

const route = useRoute()

const { setUser: setAmplitudeUser, unsetUser: unsetAmplitudeUser } = useAmplitudeTrackingService()
const { setUser: setBrazeUser } = useBrazeTrackingService()
const { setUser: setSentryUser, unsetUser: unsetSentryUser } = useSentryService()
const { getGeoIP } = useGeoService()
const { getUserSettingsFromCache, getUserBikes, getUser, syncDistanceUnit } = useUserService()
const { refreshSession, logout } = useAuthenticationService()

const config = useRuntimeConfig()

const appStoreMetaTagContent = computed(() => {
  return `app-id=${config.public.appleStoreAppId}, app-argument=${route.fullPath}`
})

useHead({
  title: 'Cardo Ride',

  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    {
      name: 'description',
      content:
        'Cardo Ride is a motorcycle platform built by motorcycle enthusiasts. It lets you track your motorcycle trips and share them with your friends and the community.'
    },
    { name: 'apple-itunes-app', content: appStoreMetaTagContent }
  ]
})

const showFollowerIntroDialog = ref(false)
const showHomebaseDialog = ref(false)
const homebaseDialogInitialValue = ref('')
const showErrorDialog = ref(false)
const errorMessage = ref('')
const dev = config.public.dev

const isLoadedFromApp = computed(() => {
  return mainStore.isLoadedFromApp
})

// Hold the cookie banner back until the app has mounted, by which point the
// embed context is known. This stops it from flashing in before we can suppress
// it in webviews.
const cookieBannerReady = ref(false)

onMounted(() => {
  cookieBannerReady.value = true
})

const rewind = computed(() => {
  const { path } = useRoute()
  return path.includes('rewind')
})

setErrorHandler(400, async (error) => {
  const code = error?.response?.data?.error?.code

  if (code === 'INAPPROPRIATE_CONTENT') {
    mainStore.showAlertModal(
      t('inappropriate_content_warning'),
      t('inappropriate-content-dialog-title')
    )
    return
  }

  if (code === 'BLOCKING_YOU') {
    mainStore.showAlertModal(t('sorry_this_user_has_blocked_you'))
    return await Promise.reject(error)
  }

  if (code === 'BLOCKED_BY_YOU') {
    mainStore.showAlertModal(t('sorry_you_have_blocked_this_user'))
    return await Promise.reject(error)
  }

  if (code === 'RENDER_LIMIT_REACHED') {
    mainStore.showAlertModal(
      t('rewind_error_one_at_a_time_message'),
      t('rewind_error_one_at_a_time_title')
    )
    return await Promise.reject(error)
  }

  if (code === 'BELOW_MINIMUM_LENGTH') {
    mainStore.showAlertModal(
      t('rewind_error_trip_too_short_message'),
      t('rewind_error_trip_too_short_title')
    )
    return await Promise.reject(error)
  }

  return await Promise.reject(error)
})

// refreshToken Logic
setErrorHandler(401, async (error) => {
  const originalRequest = error.config

  originalRequest._retry = true

  try {
    const sessionData = await refreshSession()

    await authStore.persistSession(sessionData)

    await retryRequest(originalRequest)
  } catch {
    logout()
  }
})

setErrorHandler(500, async (error) => {
  errorMessage.value = t('An unknown error occured. We are sorry for the inconvenience.')
  showErrorDialog.value = true
  return await Promise.reject(error)
})

// An explicit `?unit=` wins for the whole session: embedded views pass the unit
// they want rendered, which must not be overridden by the account setting.
const unitFromQuery =
  route.query.unit === 'metric' || route.query.unit === 'imperial' ? route.query.unit : null

// The remaining settings have no server counterpart yet and stay local-only.
if (typeof window !== 'undefined') {
  const settings = authStore.currentUser?.id
    ? getUserSettingsFromCache(authStore.currentUser.id)
    : {}

  mainStore.updateSettings(settings)

  if (unitFromQuery) {
    mainStore.updateSettings({ distance: unitFromQuery })
  }
}

useLatestUserDataOnce(async (userData) => {
  if (unitFromQuery || mainStore.isLoadedFromApp) {
    return
  }

  await syncDistanceUnit(userData)
})

useLatestUserDataOnce(async (userData) => {
  if (mainStore.isLoadedFromApp) {
    return
  }

  if (userData.auto_accept_follow_requests === null) {
    setTimeout(() => {
      showFollowerIntroDialog.value = true
    }, 5000)
  }

  // Get all bikes of the user so we can display whether one of them needs servicing
  const bikes = await getUserBikes(userData.id)

  if (bikes?.length > 0) {
    bikes.forEach((bike) => {
      garageStore.currentUserBikes.push(bike)
    })
  }
})

useGeoLocationOnce((loc) => {
  if (homebaseDialogInitialValue.value) return

  const valid = countryOptions.some((o) => o.value === loc.countryCode)
  homebaseDialogInitialValue.value = valid ? loc.countryCode : ''
}, 'geoip')

watch(
  [() => route.path, () => authStore.latestUserData],
  ([path, userData]) => {
    if (!path.startsWith('/feed')) return
    if (!userData) return
    if (userData.homebase) return
    if (showHomebaseDialog.value) return

    showHomebaseDialog.value = true
  },
  { immediate: true }
)

watch(
  [() => route.path, () => route.query.invite, () => authStore.session_data?.user?.id],
  ([path, invite]) => {
    if (!path.startsWith('/feed')) return
    if (invite === undefined) return
    if (!authStore.session_data?.user?.id) return

    mainStore.showInviteFriendModal()
  },
  { immediate: true }
)

watch(
  () => authStore.latestUserData,
  (userData, oldUser) => {
    // we are only interested in changes of the logged in user, not his / her properties
    if (oldUser?.id === userData?.id) {
      return
    }

    if (userData) {
      setAmplitudeUser(userData)
      setBrazeUser(userData)
      setSentryUser(userData)
    } else {
      unsetAmplitudeUser()
      unsetSentryUser()
    }
  },
  { immediate: true }
)

if (authStore?.session_data?.user?.id) {
  try {
    authStore.awaitAndSetLatestUserData(getUser(authStore.session_data.user.id))
  } catch (error) {
    console.error(error)
  }
}

watch(
  () => authStore?.session_data?.user?.id,
  () => {
    if (!authStore?.session_data?.user?.id) return

    try {
      authStore.awaitAndSetLatestUserData(getUser(authStore.session_data.user.id))
    } catch (error) {
      console.error(error)
    }
  },
  { immediate: true }
)

/* 
  Be aware that this event handler is not removed when the component is destroyed
  But since this is the main component it should never be unmounted
*/
onMounted(async () => {
  window.addEventListener('message', (event) => {
    if (event.data === 'subscribed') {
      // Child payment tab sends a message when the user has subscribed

      window.location.reload()
    }
  })

  const geoip = await getGeoIP()

  if (geoip) {
    mainStore.userLocation = {
      latitude: geoip.location.latitude,
      longitude: geoip.location.longitude,
      type: 'geoip',
      countryCode: geoip.country.iso_code
    }
  }
})
</script>

<style scoped>
.error {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgb(var(--color-red));
  padding: 10px;
  border-radius: 15px;
  color: white;
  font-weight: bold;
  z-index: 99999;
}

.warning {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgb(var(--color-orange));
  padding: 10px;
  border-radius: 15px;
  color: white;
  font-weight: bold;
  z-index: 99999;
}
</style>
